import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { makeUseAxios } from 'axios-hooks';
import dayjs from 'dayjs';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import store from 'store2';
import { AppointmentLookupMethod } from '../components/Appointment/Search/Lookup';
import { useKioskState } from '../context/Kiosk/kiosk';
import { useLocale, useLocalization } from '../context/Localization/localization';
import { formatForServer } from '../helpers/dateTime';
import { isElectron } from '../helpers/EnvironmentFlags/EnvironmentFlags';
import {
    TransportSubtype,
    TransportType,
    Country,
    AccountType,
    PhoneType,
    QRCodeType,
    SpgPricingType,
    MaintenanceMenuType,
    Events,
    ServiceCategory,
    HunterTireResult,
    HunterPassFail,
    SendNotificationResult,
    AppointmentLoanerOrRentalOption,
} from './Enums';
import { IconTypes } from '../controls/Icon/GenericIcon/GenericIcon.types';
import { Locales } from '../context/Localization/localization.types';
import { TextOptInStatus } from '../context/Appointment/appointment';
import { KeyExchangeStage } from '../context/Appointment/appointment.types';
import { AtLeastOne, GoMotoFunction, RequireDefined } from './GoMoto.types';
import { SelectOption } from '../controls/Select/Select/Select.types';
import clone from 'just-clone';
import { useTransportLabels } from './Labels/useTransportLabels';
import { AppointmentFilters } from '../context/AdminFilters/AdminFilters.types';
import { Dealer, Kiosk } from '../context/Kiosk/kiosk.types';

// #region API V3 endpoint
export const instance = axios.create({
    baseURL: process.env.REACT_APP_GOMOTO_API_ROOT + '/Kiosk',
    headers: {
        accept: 'application/json',
    },
    paramsSerializer: (params) => qs.stringify(params, {
        arrayFormat: 'repeat',
        serializeDate: formatForServer,
    }),
});

const formatObjectDates = (obj?: { [key: string]: any; }) => {
    typeof obj === 'object' &&
        Object.keys(obj)
            .forEach((key) => {
                if (obj[key] instanceof Date) {
                    obj[key] = formatForServer(obj[key]);
                }
            });

    return obj;
};

instance.interceptors.request.use(async (config) => {
    let { data } = config;
    const { headers } = config;
    let token = store('token');
    if (!token) {
        token = (await Authorize()).token;
        store('token', token);
    }

    token && (headers['Authorization'] = `Bearer ${token}`);

    if ((config.method === 'POST' || config.method === 'post') && typeof data === 'object') {
        data = formatObjectDates({ ...data });
    }

    return {
        ...config,
        data,
    };
});

const refreshAuthLogic = (failedRequest: any) =>
    Authorize()
        .then((tokenRefreshResponse) => {
            store('token', tokenRefreshResponse.token);
            failedRequest.response.config.headers['Authorization'] = `Bearer ${tokenRefreshResponse.token}`;

            return Promise.resolve();
        });
createAuthRefreshInterceptor(instance, refreshAuthLogic);

const axiosSuccessInterceptor = (response: AxiosResponse) => response;
const axiosErrorInterceptor = (error: any, axiosInstance: AxiosInstance) => {
    if (error.response && error.response.data) {
        console.log(
            '-- Axios Error (API Error) -- \n' +
            (error.response.config && error.response.config.url ? error.response.config.url : '') +
            '\n' +
            JSON.stringify(error.response.data),
        );
    }
    else {
        const ogRequest = error.config;

        console.log('-- Axios Error (Network Error) -- \n' + (error.config && error.config.url ? error.config.url : '') + '\n' + JSON.stringify(error));

        /**
         * If we encounter a network error, reattempt the call every 10 seconds
         * until any network issues have resolved themselves
         */
        if (!error.response && error.code !== AxiosError.ERR_CANCELED) {
            return new Promise((resolve, _reject) => {
                setTimeout(() => resolve(axiosInstance(ogRequest)), 10000);
            });
        }
    }

    return Promise.reject(error);
};

instance.interceptors.response.use(axiosSuccessInterceptor, (error) => axiosErrorInterceptor(error, instance));

const useAxios = makeUseAxios({
    axios: instance,
    cache: false,
});
// #endregion

/*
 * #region Authorization
 *  We have to use a separate instance so errors are not intercepted and cause a request loop.
 */
export const authInstance = axios.create({
    baseURL: process.env.REACT_APP_GOMOTO_API_ROOT,
    headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
    },
});
authInstance.interceptors.response.use(axiosSuccessInterceptor, (error) => axiosErrorInterceptor(error, authInstance));

const Authorize = async () => {
    const params = {
        Username: process.env.REACT_APP_GOMOTO_API_USERNAME,
        Password: process.env.REACT_APP_GOMOTO_API_KEY,
    };

    return (await authInstance.post<{ token: string; }>('/Login', params)).data;
};
// #endregion

// #region Kiosk
export interface IAppointmentResult {
    error: number;
    keySlotNumber: number;
}

/** Defines a type where at least one of the properties in the object must be defined */
export type KioskRetrievalType = AtLeastOne<{ kioskKey: string; subdomain: string; machineId: string; }>;

export const getKiosk = async ({ kioskKey, subdomain, machineId }: KioskRetrievalType) => {
    if (machineId) {
        const kioskResponse = await instance.get<Kiosk>('/GetKioskByMachineId', { params: { machineId } });

        return kioskResponse.data;
    }
    if (subdomain) {
        const kioskResponse = await instance.get<Kiosk>('/GetKioskBySubdomain', { params: { subdomain } });

        return kioskResponse.data;
    }
    else {
        const kioskResponse = await instance.get<Kiosk>('/GetKioskByKey', { params: { kioskKey } });

        return kioskResponse.data;
    }
};

export function useFetchAllKiosks(dealerKey: string) {
    const [response, reFetch] = useAxios<Kiosk[]>({}, { manual: true });

    return {
        kiosks: response.data,
        fetchAllKiosks: async () => (await reFetch({
            url: '/GetKiosksByDealer',
            params: { dealerKey },
        })).data,
    };
}

export const getAdditionalDealers = async (kioskKey: Kiosk['key']) => (await instance.get<Dealer[]>('/GetDealersByKiosk', { params: { kioskKey } })).data;

// #endregion

// #region Appointment

export interface IVehicle {
    key: string;
    year: string | null;
    make: string | null;
    model: string | null;
    chromeMake: string | null;
    chromeModel: string | null;
    series?: string;
    style?: string;
    mileage: number | null;
    vin: string;
    serviceAdvisorId?: number;
    exteriorColor?: string;
    interiorColor?: string;
    trim?: string;
    dmsVehicleKey?: string;
}

export interface IPayment {
    paymentDateTime: Date;
    paymentCardType: string;
    paymentCardNumber: string;
    paymentMessage: string;
    paymentApprovalCode: string;
    paymentMerchant: string;
    paymentMerchantId: string;
    paymentTransactionId: string;
    paymentMethod: string;
}

export type Status = 'not scheduled' | 'scheduled' | 'completed' | 'rental returned' | 'loaner returned';
export type PaymentStatus = 'no payment due' | 'paid' | 'not paid' | 'marked paid';

export type ContactPreference = 'email' | 'phone' | 'text' | 'both';
export type LicenseStatus = 'not scanned' | 'scanned';
/** Property keys used to pick transportation-specific fields from a response object. */
export type TransportationProperties = 'opCode' | 'type' | 'subtype' | 'dmsTransportationType';
export type XTimeServiceProperties = 'id' | 'goMotoId' | 'isCommonRepair' | 'name' | 'opCode' | 'price' | 'comment' | 'description';
export type KeyStatus = 'none'
    | 'slot_assigned'
    | 'picked up'
    | 'reopen requested pickup'
    | 'reopen requested dropoff'
    | 'rental key stored'
    | 'loaner key stored'
    | 'reopen requested exchange';

export interface IAppointment extends ICustomer, IVehicle, IValuation, Partial<IPayment> {
    /**
     * Remove
     */
    id: number;
    created?: Date;
    customer_service_acceptance?: boolean;
    customer_signature_s3_key?: string;
    manual_services?: string[];
    paymentEstimate?: number;
    qr_status?: string;

    // V3
    key: string;
    kioskKey: string;
    dealerKey: string;
    /** Appointment Key from the original DMS Appointment  */
    dmsAppointmentKey?: string;
    dmsAppointmentId?: number;
    dmsDealerId?: string;
    dmsDealerAppointmentKey?: string;
    appointmentDateTime: Date | string;
    promiseDateTime?: Date;
    status: Status;
    paymentStatus: PaymentStatus;
    businessName?: string;
    accountType?: AccountType;
    contactPreference?: ContactPreference;
    pickupContactPreference?: ContactPreference;
    licenseStatus: LicenseStatus;
    tradeValuationRequested?: boolean;
    signatureImage?: string;
    /**
     * Base64-encoded Letter-portrait PDF of the receipt rendered at signature time.
     * Generated by slicing an html2canvas capture into pages with section-aware
     * pagination so atomic blocks (info-grid rows, service items, signature,
     * disclaimer, customer comments) are never split across a page boundary.
     * Stored on GoMotoCore as a file asset alongside signatureImage.
     */
    receiptPdf?: string;
    customerCompletedCheckIn?: boolean;
    valuationFullScreenImage?: string;
    appointmentLookupMethod?: AppointmentLookupMethod;
    transportation?: Pick<ITransporationOptionResponse, TransportationProperties>;
    dmsCustomerKey?: string;
    dmsVehicleKey?: string;
    reminderSent?: Date;

    qrCode: string;
    /** The type of the last QR code generated for this appointment */
    qrCodeType?: QRCodeType;

    serviceAdvisorNumber?: string;
    serviceAdvisorName?: string;
    adminCreated: boolean;
    /** Indicates if the appointment was converted from a dms appointment in key management */
    convertedFromDms: boolean;
    tagNumber: string;

    // Services
    xtimeServices: Pick<IXtimeServiceBase, XTimeServiceProperties>[] | null;

    maintenanceMenusConfirmed?: MaintenanceMenu[];
    servicesConfirmed?: ServiceComment[];
    servicesRecommended?: number[];
    comment?: string;
    serviceTotal?: number;
    rndServices?: RNDService[];
    rndServicesRecommended?: string[];
    pickupTimeEntry?: string;
    pickupName?: string;
    pickupEmail?: string;
    pickupPhone?: string;

    // Locker
    keySlot?: number;
    keySlotNumber?: number;
    keyStatus: KeyStatus;
    storedByAdvisor?: boolean;
    advisorKeyStoredTimestamp?: string;
    // Survey
    customerWouldRepeat?: boolean;
    customerDidEnjoy?: boolean;

    emailUpdated?: boolean;
    phoneUpdated?: boolean;
    oldEmail?: string;
    oldPhone?: string;
    oldPhoneTypeEnum?: PhoneType;

    mileageUpdated?: boolean;

    customerInfoUpdated?: boolean;

    mobileCheckin: boolean;
    mobileCheckInComplete: boolean;
    mobileCheckInUrl: string;

    allowManualComplete: boolean;
    isHunter: boolean;

    gaveConsentToSign?: boolean;

    locale: Locales;
    roNumber?: string;

    // Tow-In Information
    towInCompanyName?: string;
    towInDriverName?: string;
    towInPhone?: string;

    // Texting
    /**
     * Whether the user opted into texting during this specific appointment.
     * Used by the `Disclaimer` control.
     */
    optedIntoTextingThisAppointment?: boolean;
    /**
     * When the user opted-in to texting on this appointment.
     */
    optInTime?: Date;
    /**
     * The texting status of the phone number on the appointment after the user has either
     * changed/confirmed it on the `CustomerDetails` component.
     */
    optedIntoTextingStatus?: TextOptInStatus;

    // Loaner/Rental
    returningLoanerOrRental?: AppointmentLoanerOrRentalOption;
    loanerOrRentalReturnTimestamp?: string;
    keyExchangeStage?: KeyExchangeStage;

    /**
     * JSON string to preserve pre-check-in data values from the Disclaimers and Disclosures pages
     * and the estimate signature on the server during mobile check-ins so we can retrieve them
     * during the latter part of such check-ins.
     */
    preCheckInData?: string;
}

export interface ServiceComment {
    id: number;
    comment?: string;
    name?: string;
    nameAltLang?: string;
    description?: string;
    descriptionAltLang?: string;
    price?: number;
    priceLow?: number;
    priceHigh?: number;
    spgPricing?: SpgPricingType;
    isTransport?: boolean;
    opCode?: string;
}

export interface MaintenanceMenuData {
    defaultScheduleOption: string | null;
    defaultMileageOption: string | null;
    menus: MaintenanceMenu[];
    scheduleOptions: MaintenanceMenuOption[];
    mileageOptions: MaintenanceMenuOption[];
}

export interface MaintenanceMenuOption {
    id: string;
    label: string;
}

export interface MaintenanceMenu {
    id: number;
    type: MaintenanceMenuType;
    title: string;
    opCode: string;
    scheduleOption: string;
    opDescriptions: string[];
    priceLow?: number;
    priceHigh?: number;
    spgPricing: SpgPricingType;
    snackable?: boolean;
}

export interface RNDServiceData {
    lastVisit: string | null;
    services: RNDService[];
}

export interface RNDService {
    opCode: string;
    description: string;
    doneElsewhere: boolean;
    price?: number;
    linkedService?: IService;
    snackable?: boolean;
}

/** Creates a new appointment */
export async function createAppointment(kioskKey: string, appointment: IAppointment, interaction?: Date) {
    return (
        await instance.post<string>(
            '/CreateAppointment',
            {
                ...appointment,
                kioskKey,
                vin: appointment.vin.toUpperCase(),
                mileage: typeof appointment.mileage === 'string' ? (parseInt(appointment.mileage, 10) || null) : appointment.mileage,
            },
            {
                params: {
                    ...(interaction ? {
                        checkinDelta: (new Date()
                            .getTime() - interaction.getTime()) / 1000,
                    } : null),
                    isKiosk: isElectron(),
                },
            },
        )
    ).data;
}

/**
 * Updates the appointment
 * @param kioskKey The kiosk key
 * @param {IAppointment} appointment The appointment to update
 * @param {Locales} locale The current locale
 */
export async function updateAppointment(
    kioskKey: string,
    appointment: IAppointment,
    locale: Locales,
    interaction?: Date,
    userID?: number,
    allowEmptyingServiceLists?: boolean,
    updateDmsAppointment?: boolean,
    precheckInComplete?: boolean,
    towInComplete?: boolean,
    keyManagement?: boolean,
) {
    return await instance.post<any>(
        '/UpdateAppointment',
        {
            ...appointment,
            kioskKey,
            locale,
            vin: appointment.vin.toUpperCase(),
            mileage: typeof appointment.mileage === 'string' ? (parseInt(appointment.mileage, 10) || null) : appointment.mileage,
        },
        {
            params: {
                locale,
                ...(interaction ? {
                    checkinDelta: (new Date()
                        .getTime() - interaction.getTime()) / 1000,
                } : null),
                isKiosk: isElectron(),
                userID,
                allowEmptyingServiceLists,
                updateDmsAppointment,
                precheckInComplete,
                towInComplete,
                keyManagement,
            },
        },
    );
}

/**
 * Interface for updatePickupAppointmentContact parameters
 */
export interface UpdatePickupAppointmentContactParams {
    appointmentKey: string;
    pickupName?: string;
    pickupEmail?: string;
    pickupPhone?: string;
    pickupContactPreference?: ContactPreference;
}

/**
 * Updates the appointment's pickup contact information
 * @param appointmentKey The appointment key
 * @param pickupName The updated name, or undefined if no change
 * @param pickupEmail The updated email, or undefined if no change
 * @param pickupPhone The updated phone number, or undefined if no change
 * @param pickupContactPreference The updated contact preference, or undefined if no change
 */
export async function updatePickupAppointmentContact({ appointmentKey, pickupName, pickupEmail, pickupPhone, pickupContactPreference }: UpdatePickupAppointmentContactParams) {
    return await instance.post<void>('/UpdatePickupAppointmentContact', {}, {
        params: {
            appointmentKey,
            ...(pickupName ? { pickupName } : undefined),
            ...(pickupEmail ? { pickupEmail } : undefined),
            ...(pickupPhone ? { pickupPhone } : undefined),
            ...(pickupContactPreference ? { pickupContactPreference } : undefined),
        },
    });
}

/**
 * Updates only the appointment's key status
 * @param appointmentKey The appointment key
 * @param keyStatus The new key status
 */
export async function updateAppointmentKeyStatus(appointmentKey: string, keyStatus: KeyStatus) {
    return await instance.post<void>('/UpdateAppointmentKeyStatus', {}, {
        params: {
            appointmentKey,
            keyStatus,
        },
    });
}

/**
 * Gets a single appointment by key
 * @param appointmentKey The appointment Key
 */
export async function getAppointment(appointmentKey: string) {
    return (await instance.get<IAppointment | null>('/GetAppointmentByKey', { params: { appointmentKey } })).data;
}

/**
 * Gets a single appointment by ROnumber
 * @param appointmentKey The appointment Key
 * @param dealerKey The dealer Key
 */
export async function getROAppointment(roNumber: string, dealerKey: string) {
    return (await instance.get<IAppointment | null>('/GetAppointmentByRONumber', {
        params: {
            roNumber,
            dealerKey,
        },
    })).data;
}

export async function getCheckInFinishedByAdvisor(appointmentKey: string) {
    return (await instance.get<boolean>('/GetCheckInFinishedByAdvisor', { params: { appointmentKey } })).data;
}

export interface PaymentDetails {
    id: string;
    status: string;
    amount: number;
    created: string | null;
    type: string;
    cardLast4: string;
    errorMessage: string;
    approvalNumber: string;
    merchantId: string;
}

export interface StripePaymentSession {
    clientSecret: string;
    id: string;
    status: 'open' | 'complete' | 'expired';
    paymentDetails: PaymentDetails | null;
}

export async function createStripePaymentSession(appointmentKey: string, dealerKey: string) {
    return (await instance.post<StripePaymentSession | null>('/CreateStripePaymentSession', {}, {
        params: {
            appointmentKey,
            dealerKey,
        },
    })).data;
}

export async function getStripePaymentSession(sessionId: string, dealerKey: string) {
    return (await instance.get<StripePaymentSession>('/GetStripePaymentSession', {
        params: {
            sessionId,
            dealerKey,
        },
    })).data;
}

export interface WorldPayTransaction {
    transactionSetupId: string;
    verificationResult: WorldPayTransactionVerificationResult | null;
    paymentStatus: IAppointment['paymentStatus'] | null;
    paymentDetails: PaymentDetails | null;
}

export interface WorldPayTransactionVerificationResult {
    suspectedFraud: boolean;
    expressResponseCode: string;
    cvvResponseCode: string;
    avsResponseCode: string;
    successfulExpressResponseCode: boolean;
    successfulCVVResponseCode: boolean;
    successfulAVSResponseCode: boolean;
    sucessfulTransaction: boolean;
}

export async function createWorldPayTransaction(appointmentKey: string, dealerKey: string, billingStreet: string, billingZip: string, billingCity: string, billingState: string) {
    return (await instance.post<WorldPayTransaction | null>('/CreateWorldPayTransaction', {}, {
        params: {
            appointmentKey,
            dealerKey,
            billingStreet,
            billingZip,
            billingCity,
            billingState,
        },
    })).data;
}

export async function verifyWorldPayTransaction(
    transactionId: string,
    validationCode: string,
    expressResponseCode: string,
    cvvResponseCode: string,
    avsResponseCode: string,
    dealerKey: string,
    appointmentKey: string,
) {
    return (await instance.post<WorldPayTransaction | null>('/VerifyWorldPayTransaction', {}, {
        params: {
            transactionId,
            validationCode,
            expressResponseCode,
            cvvResponseCode,
            avsResponseCode,
            dealerKey,
            appointmentKey,
        },
    })).data;
}

export async function expireWorldPayTransaction(appointmentKey: string, dealerKey: string) {
    return (await instance.post<void>('/ExpireWorldPayTransaction', {}, {
        params: {
            appointmentKey,
            dealerKey,
        },
    })).data;
}

export async function upsertTextingOptIn(appointmentKey: string, phone: string, optIn: boolean, dealerKey: string) {
    return (await instance.post<void>('/UpsertOptIn', {}, {
        params: {
            appointmentKey,
            phone,
            optIn,
            dealerKey,
        },
    })).data;
}

type DMSAppointmentParams = {
    kioskKey: Kiosk['key'];
    dealerKey?: Dealer['key'];
    dmsAppointmentKey?: string;
    appointmentNumber?: number;
} & (
        | {
            dmsAppointmentKey: string;
            appointmentNumber?: never;
        }
        | {
            appointmentNumber: number;
            dmsAppointmentKey?: never;
        }
    );

/**
 * Gets a single DMS appointment by DMS key or number
 * @param param.kioskKey The kiosk Key
 * @param param.dealerKey The dealer Key
 * @param param.dmsAppointmentKey The DMS appointment Key. Cannot be used with @see param.appointmentNumber
 * @param param.appointmentNumber The DMS appointment number value. Cannot be used with @see param.dmsAppointmentKey
 */
export async function getDMSAppointment({ kioskKey, dealerKey, dmsAppointmentKey, appointmentNumber }: DMSAppointmentParams) {
    if (appointmentNumber !== undefined) {
        return (
            await instance.get<IAppointment | null>('/GetDMSAppointmentByNumber', {
                params: {
                    kioskKey,
                    dealerKey,
                    appointmentNumber,
                },
            })
        ).data;
    }
    else {
        return (
            await instance.get<IAppointment | null>('/GetDMSAppointmentByKey', {
                params: {
                    kioskKey,
                    dealerKey,
                    dmsAppointmentKey,
                },
            })
        ).data;
    }
}

/**
 * Gets all dealers at a kiosk with the same DMS configuration as a given dealer.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key
 * @returns An array of dealer keys with the same DMS configuration as `dealerKey` for the kiosk `kioskKey`
 */
export async function getDealersWithSameDMS(kioskKey: string, dealerKey: string) {
    return (await instance.get<Array<string> | null>('/GetDealersWithSameDms', {
        params: {
            kioskKey,
            dealerKey,
        },
    })).data;
}

/**
 * Gets a dealer key from this kiosk's available dealers matching the given DMS details.
 * @param kioskKey The kiosk key
 * @param dmsDealerKey The DMS dealer Key
 * @param dmsStoreId The DMS store ID
 * @param dmsAreaId The DMS area ID
 * @returns A non-DMS dealer key for this kiosk matching the DMS details, if available
 */
export async function getDealerKeyFromDMS(kioskKey: string, dmsDealerKey: string, dmsStoreId?: string, dmsAreaId?: string) {
    return (await instance.get<string | null>('/GetDealerKeyFromDms', {
        params: {
            kioskKey,
            dmsDealerKey,
            dmsStoreId,
            dmsAreaId,
        },
    })).data;
}

/**
 * The appointment filter object used by the `SearchForm` component to look-up an appointment.
 *
 * @extends IAppointment - {@link IAppointment} description.
 */
export interface AppointmentFilter extends Pick<IAppointment, 'phone' | 'email' | 'firstName' | 'lastName'> {
    /**
     * Redfined {@link IAppointment.businessName businessName} to not be an optional property.
     */
    businessName: RequireDefined<IAppointment['businessName']>;
}

/**
 * The params object sent in the `get` request to search for DMS Appointments.
 *
 * @extends AppointmentFilter - {@link AppointmentFilter} description.
 * @extends IAppointment - {@link IAppointment} description.
 */
export interface AppointmentSearchParams extends AppointmentFilter, Pick<IAppointment, 'kioskKey' | 'dealerKey'> {
    /**
     * When true, the params are being sent from the physical kiosk rather than a mobile device.
     */
    isKiosk: boolean;
}

/**
 * Searches for appointments based on the filter.
 * @param kioskKey The kiosk Key
 * @param filter Filter for searching
 */
export const appointmentSearch: GoMotoFunction<[string, string, AppointmentFilter], Promise<IAppointment[]>> = async (kioskKey, dealerKey, filter) => {
    filter = {
        phone: filter.phone.trim(),
        email: filter.email.trim(),
        firstName: filter.firstName.trim(),
        lastName: filter.lastName.trim(),
        businessName: filter.businessName.trim(),
    };

    const params: AppointmentSearchParams = {
        kioskKey,
        dealerKey,
        ...filter,
        isKiosk: isElectron(),
    };

    return (await instance.get<IAppointment[]>('/GetDmsAppointments', { params })).data.map((appointment: IAppointment) => ({
        ...appointment,
        dmsVehicleKey: appointment.dmsVehicleKey || appointment.vin,
    }));
};

type AppointmentCustomerFilter = {
    phone: string;
    email: string;
};

export type AppointmentOrCustomer = {
    appointments?: IAppointment[];
    customers?: ICustomer[];
};

/**
 * Searches for appointments based on the filter.
 * @param kioskKey The kiosk Key
 * @param filter Filter for searching
 */
export async function mergedAppointmentCustomerSearch(kioskKey: string, dealerKey: string, filter: AppointmentCustomerFilter) {
    filter = {
        phone: filter.phone.trim(),
        email: filter.email.trim(),
    };

    const params = {
        kioskKey,
        dealerKey,
        ...filter,
        isKiosk: isElectron(),
    };

    return (await instance.get<AppointmentOrCustomer>('/GetDmsAppointmentsOrCustomers', { params })).data;
}

/**
 * Gets the QR Code for the appointment.
 * @param {string} appointmentKey The appointment key
 * @param {Locales} locale The current locale
 * @param mobileCheckInComplete Whether the qr code should be generated to save the appointment or complete the appointment.
 * This must be true if currentUrl is empty.
 * @param currentUrl The current url to be saved to the qr code.
 * This must be set if mobileCheckInComplete is false.
 */
export const sendMobileCheckInEmail = async (
    appointmentKey: string,
    dealerKey: string,
    locale: Locales,
    mobileCheckInComplete: boolean,
    currentUrl?: string,
    maskPhone?: boolean,
    maskEmail?: boolean,
    maskVin?: boolean,
    phoneType?: PhoneType,
) => {
    const params = {
        appointmentKey,
        dealerKey,
        mobileCheckInComplete,
        currentUrl,
        locale,
        maskPhone,
        maskEmail,
        maskVin,
        phoneType,
    };

    return (await instance.get<string>('/SendMobileCheckInEmail', { params })).data;
};

/**
 * Generates the QR Code for the appointment, does not send an email.
 * @param {string} appointmentKey The appointment key
 * @param mobileCheckInComplete Whether the qr code should be generated to save the appointment or complete the appointment.
 * This must be true if currentUrl is empty.
 * @param currentUrl The current url to be saved to the qr code.
 * @param lockerNumber the locker number to generate QR code to open
 * This must be set if mobileCheckInComplete is false.
 */
export const generateAppointmentQRCode = async (
    appointmentKey: string,
    dealerKey: string,
    locale: Locales,
    mobileCheckInComplete: boolean,
    currentUrl?: string,
    lockerNumber?: number,
    maskPhone?: boolean,
    maskEmail?: boolean,
    maskVin?: boolean,
    phoneType?: PhoneType,
    is365?: boolean,
) => {
    const params = {
        appointmentKey,
        dealerKey,
        mobileCheckInComplete,
        currentUrl,
        locale,
        lockerNumber,
        maskPhone,
        maskEmail,
        maskVin,
        phoneType,
        is365,
    };

    return (await instance.get<string>('/GenerateAppointmentQRCode', { params })).data;
};

/**
 * Generates the QR Code for picking up a vehicle.
 * @param {string} appointmentKey The appointment key
 */
export const generateVehicleReadyQRCode = async (appointmentKey: string) => {
    const params = { appointmentKey };

    return appointmentKey ? (await instance.get<string>('/GenerateVehicalReadyQRCode', { params })).data : '';
};

/**
 * Sends a link to continue the check-in process for the outdoor kiosk
 */
export const useSendAppointmentCheckInEmail = () => {
    const locale = useLocale();

    return async (filter: { kioskKey: Kiosk['key']; dealerKey: Dealer['key']; } & Pick<IAppointment, 'email'>) => {
        const params = {
            ...filter,
            locale,
        };

        const response = (await instance.get<string>('/SendAppointmentCheckInEmail', { params })).data;

        if (!!response) {
            if (response === 'APPOINTMENT_NOT_FOUND') {
                throw response;
            }
            else if (response !== 'NOT_ENABLED') {
                return response;
            }
        }

        return;
    };
};

/**
 * Resends the check-in receipt
 */
export const useSendCheckInReceipt = () => {
    const locale = useLocale();

    return async (
        filter:
            | { appointmentKey: IAppointment['key']; dealerKey: IAppointment['dealerKey']; }
            | ({ kioskKey: Kiosk['key']; } & Pick<IAppointment, 'firstName' | 'lastName' | 'email'>)
            | ({ kioskKey: Kiosk['key']; } & Pick<IAppointment, 'businessName' | 'email'>),
    ) => {
        const params = {
            ...filter,
            locale,
        };

        const error = (await instance.get<string>('/SendCheckInReceipt', { params })).data;

        if (error && error === 'APPOINTMENT_NOT_FOUND') {
            throw error;
        }

        return;
    };
};

export interface SendPickupReceiptParams {
    appointmentKey?: IAppointment['key'];
    locale?: IAppointment['locale'];
    fallbackToEmail?: boolean;
    sendText: boolean;
    sendEmail: boolean;
    userId?: number;
    /**
     * Indicates if the pickup notification is an attempt to resend
     * the notification. Not passed as a param in the endpoint, but
     * decides what endpoint to hit.
     */
    resend?: boolean;
    shouldUsePickupPhone?: boolean;
    shouldUsePickupEmail?: boolean;
}

export type SendPickupReceiptHook = GoMotoFunction<
    [void],
    GoMotoFunction<[SendPickupReceiptParams], Promise<SendNotificationResponse>>
>;

/**
 * Resends the pick-up receipt
 */
export const useSendPickupReceipt: SendPickupReceiptHook = () => {
    const locale = useLocale();

    return async ({ resend, ...params }) => {
        params = {
            ...params,
            locale: params.locale ?? locale,
        };

        return (await instance.get<SendNotificationResponse>(resend ? '/SendQRCode' : '/SendPickupReceipt', { params })).data;
    };
};

/**
 * Get the locker open status
 * @param kioskKey the kiosk key
 * @param lockerNumber the locker number
 */
export async function getIsLockerOpen(kioskKey: string, lockerNumber: number) {
    return (await instance.get<boolean>('/IsLockerOpen', {
        params: {
            kioskKey,
            lockerNumber,
        },
    })).data;
}

export interface IKeySlot {
    id: number;
    kioskKey: string;
    keySlotNumber: number;
    appointmentKeyStatus: string;
    isOpen: boolean;
}

/**
 * Gets the kiosk and slot information for the appointment
 * @param appointmentKey the apt key
 */
export async function getAppointmentKeySlot(appointmentKey: string) {
    return (await instance.get<IKeySlot>('/GetAppointmentKeySlot', { params: { appointmentKey } })).data;
}

/**
 * Retrieves an available locker
 * @param kioskKey the kiosk key
 * @param lockerNumber the locker number
 */
export async function retrieveAvailableLocker(kioskKey: string, dealerKey: string, appointmentKey: string, lockerNumber: number = 0) {
    return (await instance.get<IAppointmentResult>('/RetrieveAvailableLocker', {
        params: {
            kioskKey,
            dealerKey,
            appointmentKey,
            lockerNumber,
        },
    })).data;
}

/**
 * Records that a key was stored in a locker for the given appointment. Updates the appointment's
 * kioskKey/keyStatus/keySlot, marks the keyTable slot occupied with the appointmentKey association,
 * sends any check-in notification that was suppressed during a paired mobile-then-physical check-in,
 * and inserts the corresponding audit-trail event.
 *
 * @param kioskKey the key of the kiosk where the key was actually stored (the physical kiosk)
 * @param appointmentKey the appointment whose key is being stored
 * @param lockerNumber the locker the key was placed into
 * @param userId set when an admin stores the key on behalf of a customer; omit for the customer flow
 */
export async function appointmentKeyStored(kioskKey: string, appointmentKey: IAppointment['key'], lockerNumber: number, userId?: number) {
    return (await instance.post<IKeySlot>('/AppointmentKeyStored', null, {
        params: {
            kioskKey,
            appointmentKey,
            lockerNumber,
            ...(userId != null ? { userId } : undefined),
        },
    })).data;
}

// #endregion
export interface SendLostQrCodeParams {
    appointmentKey?: IAppointment['key'];
    kioskKey?: Kiosk['key'];
    email?: IAppointment['email'];
    phone?: IAppointment['phone'];
    locale?: IAppointment['locale'];
    fallbackToEmail?: boolean;
    sendText: boolean;
    sendEmail: boolean;
    userId?: number;
}

export interface SendNotificationResponse {
    dealerKey: string | null;
    appointmentKey: string | null;
    dealerName: string | null;
    dealerTextNumber: string | null;
    result: SendNotificationResult;
    shouldUsePickupPhone: boolean;
    shouldUsePickupEmail: boolean;
}

/**
 * Resends the qr code email based on the status of the appointment
 */
export const useSendLostQrCode = () => {
    const locale = useLocale();

    return async (params: SendLostQrCodeParams): Promise<SendNotificationResponse> => {
        params = {
            ...params,
            locale: params.locale ?? locale,
        };

        return (await instance.get<SendNotificationResponse>('/SendQrCode', { params })).data;
    };
};

// #endregion

// #region Customer
export interface ICustomer {
    key: string;
    firstName: string;
    lastName: string;
    businessName?: string;
    accountType?: AccountType;
    businessType?: string;
    city: string;
    street: string;
    streetLine2: string;
    postalCode: string;
    stateOrProvince: string;
    email: string;
    phone: string;
    cellPhone?: string;
    phoneTypeEnum?: PhoneType;
    allPhones?: { [key: string]: string; }[];
    contactPreference?: ContactPreference;
    dmsCustomerKey?: string;
}

export interface ICustomerWithVehicles extends ICustomer {
    vehicles?: Array<Omit<IVehicle, 'year'> & { year: number; }>;
}

export interface ICityAndState {
    city: string;
    stateOrProvince: string;
}

export const getCityAndState = async (postalCode: string) => (await instance.get<ICityAndState>('/GetCityAndState', { params: { postalCode } })).data;

export interface IMakeModel {
    make: string;
    model: string;
}

export const getMakeModel = async (vin: string) => (await instance.get<IMakeModel>('/GetMakeModel', { params: { vin } })).data;

/**
 * Searches for customers by phone number.
 * @param kioskKey The kiosk Key
 * @param filter Filter for searching
 */
export const getCustomers = async (
    kioskKey: string,
    dealerKey: string,
    filter: { phone: string; email: string; firstName: string; lastName: string; business: string; },
) => {
    filter = {
        phone: filter.phone.trim(),
        email: filter.email.trim(),
        firstName: filter.firstName.trim(),
        lastName: filter.lastName.trim(),
        business: filter.business.trim(),
    };

    const params = {
        kioskKey,
        dealerKey,
        ...filter,
        isKiosk: isElectron(),
        includeVehicleData: true,
    };

    return (await instance.get<Array<ICustomerWithVehicles>>('/GetCustomers', { params })).data;
};

export const updateDMSCustomer = async (kioskKey: Kiosk['key'], appointment: IAppointment) => {
    appointment.firstName = appointment.firstName ? appointment.firstName.trim() : '';
    appointment.lastName = appointment.lastName ? appointment.lastName.trim() : '';
    appointment.email = appointment.email ? appointment.email.trim() : '';

    return (await instance.post<string>('/UpdateDmsCustomer', {
        ...appointment,
        kioskKey,
    }, { params: { kioskKey } })).data;
};
// #endregion

// #region Audit Trail

export const insertAuditTrail = async (appointmentKey: IAppointment['key'], eventID: Events, userID?: number, mergeField?: string) => {
    if (appointmentKey && appointmentKey !== '') {
        await instance.post<void>('/InsertAuditTrail', {}, {
            params: {
                appointmentKey,
                eventID,
                userID,
                mergeField,
            },
        });
    }
};
/*
 * #endregion
 * #region Service Details
 */
export interface IServiceDetail {
    laborPrice?: number | undefined;
    partsPrice?: number | undefined;
    key: string;
    description: string;
    opCode?: string;
    opCodeDescription: string;
    comment?: string;
    price: number;
    childServices: Array<{ description?: string; price?: number; }>;
    lineCode?: string;
    lineNumber?: string;
    laborType?: string;
    repeatRepair?: boolean;
}

/**
 * Fetches the service details for the appointment.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key
 * @param dmsAppointmentKey The DMS appointment key
 * @param keydropAppointmentKey The internal appointment key
 */
export async function getServiceDetails(kioskKey: string, dealerKey: string, dmsAppointmentKey: string, keydropAppointmentKey: string) {
    const params = {
        kioskKey,
        dealerKey,
        dmsAppointmentKey,
        keydropAppointmentKey,
    };

    return (await instance.get<IServiceDetail[]>('/GetServiceDetails', { params: params })).data || [];
}

/**
 * Updates a service detail item.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key
 * @param dmsAppointmentKey The key for the dms appointment
 * @param keydropAppointmentKey The key for the keytrack appointment
 */
export const freezeServiceDetails = (kioskKey: string, dealerKey: string, dmsAppointmentKey: string, keydropAppointmentKey: string) =>
    instance.post<void>('/FreezeServiceDetails', {}, {
        params: {
            kioskKey,
            dealerKey,
            dmsAppointmentKey,
            keydropAppointmentKey,
        },
    });

/**
 * Updates a service detail item.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key
 * @param detail The service detail to update
 */
export const updateServiceDetail = (kioskKey: Kiosk['key'], dealerKey: Dealer['key'], { key: serviceKey, description: serviceDescription }: IServiceDetail) =>
    instance.post<void>('/UpdateServiceDetail', {}, {
        params: {
            kioskKey,
            dealerKey,
            serviceKey,
            serviceDescription,
        },
    });

/**
 * Deletes a service detail item.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key
 * @param detail The service detail to delete
 */
export const deleteServiceDetail = (kioskKey: Kiosk['key'], dealerKey: Dealer['key'], { key: serviceKey }: IServiceDetail) =>
    instance.delete<void>('/DeleteServiceDetail', {
        params: {
            kioskKey,
            dealerKey,
            serviceKey,
        },
    });
// #endregion

// #region Recall
export interface IRecall {
    title: string;
    description: string;
}

/**
 * Retrieves recalls for the provided VIN.
 * @param kioskKey The kiosk Key
 * @param vin Vin
 */
export const getRecalls = async (kioskKey: string, appointmentKey: string, vin: string) =>
    (await instance.get<IRecall[]>('/GetRecallsByVin', {
        params: {
            kioskKey,
            appointmentKey,
            vin,
        },
    })).data;

// #endregion

// #region Service
export interface ISpgPricingData {
    opCode: string;
    priceLow?: number;
    priceHigh?: number;
    isSpgOp: boolean;
}

export interface IService extends Partial<ISpgPricingData>, IServiceLocalized, Partial<IServiceLocalizedAltLang> {
    id: number;
    category: ServiceCategory;
    genericPrice1?: number;
    genericPrice2?: number;
    isGenericUpsell?: boolean;
    /** Path to a local image */
    imagePath?: string;
    isCommon?: boolean;
    isCommonRepair?: boolean;
    opCode?: string;
    price?: number;
    isUpsellPartB?: boolean;
    /** True if the service is transportation related */
    isTransport?: boolean;

    /** Comment provided by customer, not from server */
    comment?: string;

    /** Should show as a snack*/
    snackable?: boolean;

    useSpgPricing?: boolean;
    spgPricing?: SpgPricingType;

    showInCart?: boolean;
}

interface IServiceLocalized {
    name: string;
    description?: string;
    genericDisclaimer?: string;
    genericPriceDisclaimer?: string;
    genericPriceTitle1?: string;
    genericPriceTitle2?: string;
    priceDetails?: string;
    /** URL to a remove image */
    imageUrl: string;
}

interface IServiceLocalizedAltLang {
    nameAltLang: string;
    descriptionAltLang: string;
    genericDisclaimerAltLang: string;
    genericPriceDisclaimerAltLang: string;
    genericPriceTitle1AltLang: string;
    genericPriceTitle2AltLang: string;
    priceDetailsAltLang: string;
    /** ULR to a remove image in the AltLang */
    imageUrlAltLang: string;
}

export type LocalizedService = Omit<IService, keyof IServiceLocalized | keyof IServiceLocalizedAltLang> & { localization: IServiceLocalized; full: IService; };

/**
 * Returns a factory that creates a LocalizedService object from a Service.
 */
export const useLocalizedServiceFactory = () => {
    const locale = useLocale();
    const {
        dealer: { primaryLanguage },
    } = useKioskState();

    return useCallback(
        (service: IService): LocalizedService => {
            if (locale === primaryLanguage) {
                const localization = {
                    name: service.name,
                    description: service.description,
                    genericDisclaimer: service.genericDisclaimer,
                    genericPriceDisclaimer: service.genericPriceDisclaimer,
                    genericPriceTitle1: service.genericPriceTitle1,
                    genericPriceTitle2: service.genericPriceTitle2,
                    priceDetails: service.priceDetails,
                    imageUrl: service.imageUrl,
                };

                return {
                    ...service,
                    localization,
                    full: service,
                };
            }
            else {
                const localization = {
                    name: service.nameAltLang || service.name,
                    description: service.descriptionAltLang || service.description,
                    genericDisclaimer: service.genericDisclaimerAltLang || service.genericDisclaimer,
                    genericPriceDisclaimer: service.genericPriceDisclaimerAltLang || service.genericPriceDisclaimer,
                    genericPriceTitle1: service.genericPriceTitle1AltLang || service.genericPriceTitle1,
                    genericPriceTitle2: service.genericPriceTitle2AltLang || service.genericPriceTitle2,
                    priceDetails: service.priceDetailsAltLang || service.priceDetails,
                    imageUrl: service.imageUrlAltLang || service.imageUrl,
                };

                return {
                    ...service,
                    localization,
                    full: service,
                };
            }
        },
        [locale, primaryLanguage],
    );
};

/**
 * Localizes a service object.
 * @param service Service to localize
 */
export const useLocalizedService = (service?: IService) => {
    const factory = useLocalizedServiceFactory();

    return service && factory(service);
};

/**
 * Retrieves a single service for the op code.
 * @param kioskKey The kiosk Key
 * @param opCode The op code for the service
 */
export async function getService(kioskKey: string, opCode: string): Promise<IService>;
export async function getService(kioskKey: string, opCode: string | string[]) {
    const opCodes = typeof opCode === 'string' ? [opCode] : opCode;
    const results = (await instance.get<IService[]>('/GetServices', {
        params: {
            kioskKey,
            opCode: opCodes,
        },
    })).data;

    return (!!results?.length && typeof opCode === 'string' ? results[0] : results) || undefined;
}

/*
 * Gets the list of all services.
 * @param kioskKey The kiosk Key
 * @param year The Vehicle year
 * @param make The Vehicle make
 */
export const getAllServices = async (kioskKey: string, year = -1, make = '') =>
    (await instance.get<IService[]>('/GetAllServices', {
        params: {
            kioskKey,
            year: isNaN(year) ? '' : year,
            make,
            limit: 1000,
        },
    })).data;

export const getMaintenanceMenus = async (
    kioskKey: string,
    dealerKey: string,
    vin: string,
    make: string,
    model: string,
    year: number,
    mileage?: number,
    scheduleOption?: string | null,
    mileageOption?: string | null,
) =>
    (
        await instance.get<MaintenanceMenuData>('/GetMaintenanceMenus', {
            params: {
                kioskKey,
                dealerKey,
                vin,
                year: isNaN(year) ? '' : year,
                make,
                model,
                mileage,
                scheduleOption,
                mileageOption,
            },
        })
    ).data;

export const getSpgPricing = async (
    kioskKey: string,
    dealerKey: string,
    opCodes: string[],
    vin: string,
    year: number,
    make: string,
    model: string,
    mileage: number | null,
) => (await instance.get('/GetSpgPricing', {
    params: {
        kioskKey,
        dealerKey,
        opCode: opCodes,
        vin,
        year: isNaN(year) ? '' : year,
        make,
        model,
        mileage,
    },
})).data;

export const getRNDServices = async (kioskKey: string, dealerKey: string, vin: string, mileage?: number) =>
    (
        await instance.get<RNDServiceData>('/GetRNDServices', {
            params: {
                kioskKey,
                dealerKey,
                vin,
                mileage,
            },
        })
    ).data;

// #endregion

// #region Hunter
export interface IHunterTireResult {
    asymmetric: number;
    image?: string;
    camberResult: number;
    camberValue: number;
    insideShoulder: number;
    outsideShoulder: number;
    overinflation: number;
    underinflation: number;
    tire1?: string;
    tire1Result: HunterTireResult;
    tire2?: string;
    tire2Result: HunterTireResult;
    tire3?: string;
    tire3Result: HunterTireResult;
    tire4?: string;
    tire4Result: HunterTireResult;
}

export interface IHunterVehicle {
    actualStoppingDistance: number;
    idealStoppingDistance: number;
    drivingTooFast: boolean;
    drivingTooSlow: boolean;
    frontTotalToeResult: number;
    frontTotalToeValue: number;
    rearTotalToeResult: number;
    rearTotalToeValue: number;
    tireCheckValue: HunterPassFail;
    image: string;
    make: string;
    model: string;
    spec: string;
    speed: string;
    year: number;
    vin: string;
    wheelAlignmentValue: HunterPassFail;
    frontLeftResult: IHunterTireResult;
    frontRightResult: IHunterTireResult;
    rearLeftResult: IHunterTireResult;
    rearRightResult: IHunterTireResult;
}

/**
 * Retrieves the hunter vehicle data for the provided VIN
 * @param kioskKey The kiosk Key
 * @param vin The vehicle VIN
 */
export const getHunterVehicle = async (dealerKey: string, vin: string) =>
    (await instance.get<IHunterVehicle>('/GetHunterData', {
        params: {
            dealerKey,
            vin,
        },
    })).data;
// #endregion

// #region Upsell

/**
 * Retrieves upsell services for the vehicle.
 * @param kioskKey The kiosk Key
 * @param year The vehicle year
 * @param make The vehicle make
 * @param model The vehicle model
 * @param mileage The vehicle mileage
 * @param includeUpsellPartB whether to retrieve part B items
 */
export const useUpsellServices = (kioskKey: string, year: number, make: string, model: string, mileage = -1, includeUpsellPartB = false) => {
    const params = {
        kioskKey,
        year: isNaN(year) ? '' : year,
        make,
        model,
        mileage,
        includeUpsellPartB,
        includeCustom: isElectron(),
    };
    const [response] = useAxios<IService[]>({
        url: '/GetUpsellServices',
        params,
    });

    return response;
};
// #endregion

// #region Xtime
export interface IXtimeServiceBase {
    id: number;
    goMotoId: number;
    type: 'standard' | 'menu';
    menuId?: number;
    name: string;
    opCode?: string;
    isCommonRepair: boolean;
    price: number;
    description: string;

    /** Comment provided by customer, not from server */
    comment?: string;

    /** Should show as a snack*/
    snackable?: boolean;
}

export interface IXtimeStandardService {
    id: number;
    goMotoId: number;
    dmsOpcode: string;
    price: number;

    description: string;
    reservableByCustomer: boolean;
    type: 'repair' | 'maintenance';
    name: string;
}

export interface IXtimeMenuService {
    id: number;
    goMotoId: number;
    dmsOpcode: string;
    price: number;

    description: string;
    name: string;
    order: number;
    selectByDefault: boolean;
    selectable: boolean;
}

export interface IXtimeMenuType {
    id: number;
    services: IXtimeMenuService[];
    name: string;
    totalPrice: number;

    /** Should show as a snack*/
    snackable?: boolean;
}

export interface IXtimeRecommended {
    mileage: number;
    menuTypes: IXtimeMenuType[];
}

/**
 * Retrieves recommended Xtime services for the vehicle.
 * @param kioskKey The kiosk Key
 * @param dealerKey The dealer Key
 * @param make The vehicle make
 * @param mileage The vehicle mileage
 * @param vin The vehicle VIN
 */
export async function getRecommendedXtimeServices(kioskKey: string, dealerKey: string, make: string, mileage: number, vin: string) {
    const params = {
        kioskKey,
        dealerKey,
        make,
        mileage,
        vin,
    };

    return (await instance.get<IXtimeRecommended>('/GetRecommendedXtimeServices', { params })).data;
}

/**
 * Retrieves standard Xtime services for the vehicle.
 * @param kioskKey The kiosk Key
 * @param dealerKey The dealer Key
 * @param make The vehicle Make
 * @param vin The vehicle VIN
 */
export async function getXtimeServices(kioskKey: string, dealerKey: string, make: string, vin: string) {
    const params = {
        kioskKey,
        dealerKey,
        make,
        vin,
    };

    return (await instance.get<IXtimeStandardService[]>('/GetStandardXtimeServices', { params })).data;
}

// #endregion

// #region Time Table
export interface IHybridTimeTable {
    day: number;
    startTime: string;
    endTime: string;
}

/**
 * Retrieves the time ranges for when this kiosk is open.
 * @param kioskKey The kiosk Key
 */
export const getServiceOpenTimeRanges = async (kioskKey: string) =>
    (await instance.get<IHybridTimeTable[] | null>('/GetServiceOpenTimeRanges', { params: { kioskKey } })).data;
// #endregion

/*
 * #region Admin
 *  TODO: I don't want my changes to affect the KeyMap, but this function should be replaced with getActiveAppointments.
 */
export async function appointmentSearchAdmin(filters: AppointmentFilters) {
    const date = filters.start && {
        start: dayjs(filters.start)
            .startOf('day')
            .format('YYYY-MM-DD'),
        end: dayjs(filters.end || filters.start)
            .endOf('day')
            .format('YYYY-MM-DD'),
    };

    const params = {
        kioskKey: filters.kioskKey,
        adminCreated: filters.adminCreated,
        name: filters.name,
        keySlotEmpty: filters.keySlotEmpty,
        startDate: date?.start,
        endDate: date?.end,
        storedByAdvisor: filters.storedByAdvisor,
        keyStatus: filters.keyStatus,
        sessionId: filters.sessionId,
    };

    return (await instance.get<IAppointment[]>('/GetActiveAppointments', { params })).data;
}

// #region GetActiveAppointments
export interface GetActiveAppointmentsRequest {
    kioskKey: Kiosk['key'];
    dealerKey?: Dealer['key'];
    adminCreated?: boolean;
    name?: string;
    keySlotEmpty?: boolean;
    startDate?: string;
    endDate?: string;
    storedByAdvisor?: boolean;
    keyStatus?: string[];
    sessionId?: string;
}

export const getActiveAppointments: GoMotoFunction<[AppointmentFilters], Promise<IAppointment[]>> = async (filters) => {
    const params: GetActiveAppointmentsRequest = {
        kioskKey: filters.kioskKey,
        dealerKey: filters.dealerKey,
        adminCreated: filters.adminCreated,
        name: filters.name,
        keySlotEmpty: filters.keySlotEmpty,
        startDate: filters.start ? dayjs(filters.start)
            .startOf('day')
            .format('YYYY-MM-DD') : undefined,
        endDate: filters.start ? dayjs(filters.end ?? filters.start)
            .endOf('day')
            .format('YYYY-MM-DD') : undefined,
        storedByAdvisor: filters.storedByAdvisor,
        keyStatus: filters.keyStatus,
        sessionId: filters.sessionId,
    };

    const response: AxiosResponse<IAppointment[]> = await instance.get<IAppointment[]>('/GetActiveAppointments', { params });

    return response.data;
};
// #endregion

// #region GetPriorityAppointments
export type GetPriorityAppointmentsProps = Omit<AppointmentFilters, 'adminCreated' | 'keySlotEmpty' | 'storedByAdvisor' | 'keyStatus'>;

export interface GetPriorityAppointmentsRequest {
    kioskKey: Kiosk['key'];
    dealerKey?: Dealer['key'];
    name?: string;
    startDate?: string;
    endDate?: string;
    sessionId?: string;
}

export const getPriorityAppointments: GoMotoFunction<[GetPriorityAppointmentsProps], Promise<IAppointment[]>> = async (filters) => {
    const params: GetPriorityAppointmentsRequest = {
        kioskKey: filters.kioskKey,
        dealerKey: filters.dealerKey,
        name: filters.name,
        startDate: filters.start ? dayjs(filters.start)
            .startOf('day')
            .format('YYYY-MM-DD') : undefined,
        endDate: filters.start ? dayjs(filters.end ?? filters.start)
            .endOf('day')
            .format('YYYY-MM-DD') : undefined,
        sessionId: filters.sessionId,
    };

    const response: AxiosResponse<IAppointment[]> = await instance.get<IAppointment[]>('/GetPriorityAppointments', { params });

    return response.data;
};
// #endregion

// #region GetDmsAppointments
export type GetDmsAppointmentsProps = Omit<AppointmentFilters, 'adminCreated' | 'keySlotEmpty' | 'storedByAdvisor' | 'keyStatus'>;

export interface GetDmsAppointmentsRequest {
    kioskKey: Kiosk['key'];
    dealerKey?: Dealer['key'];
    name?: string;
    startDate?: string;
    endDate?: string;
    filterInternalAppt: true;
    sessionId?: string;
    includeDeleted?: boolean;
}

export const getDmsAppointments: GoMotoFunction<[GetDmsAppointmentsProps], Promise<IAppointment[]>> = async (filters) => {
    const dmsFilters: GetDmsAppointmentsProps = clone(filters);
    const today: Date = new Date();

    if (!dmsFilters.start && !dmsFilters.end) {
        dmsFilters.start = dayjs()
            .startOf('week')
            .toDate();
        dmsFilters.end = dayjs()
            .endOf('week')
            .toDate();
    }

    const params: GetDmsAppointmentsRequest = {
        kioskKey: dmsFilters.kioskKey,
        dealerKey: dmsFilters.dealerKey,
        name: dmsFilters.name,
        startDate: dmsFilters.start ? dayjs(dmsFilters.start)
            .startOf('day')
            .format('YYYY-MM-DD') : undefined,
        endDate: dmsFilters.start ? dayjs((dmsFilters.end ?? dmsFilters.start) < today ? (dmsFilters.end ?? dmsFilters.start) : today)
            .endOf('day')
            .format('YYYY-MM-DD') : undefined,
        filterInternalAppt: true,
        sessionId: filters.sessionId,
        includeDeleted: true,
    };

    const response: AxiosResponse<IAppointment[]> = await instance.get<IAppointment[]>('/GetDmsAppointments', { params });

    return response.data.map((appointment: IAppointment) => {
        return {
            ...appointment,
            dmsVehicleKey: appointment.dmsVehicleKey ?? appointment.vin,
        };
    });
};
/*
 * #endregion
 * #endregion
 */

// #region KeyTable
export interface IKeyTable {
    id: number;
    keySlotNumber: number;
    isOccupied: boolean;
    appointmentKey?: IAppointment['key'];
    storedByAdvisor: boolean;
    isKeyExchange?: boolean;
}
export const useKeyTable = () => {
    const { key } = useKioskState();

    return {
        /** Fetches the key table for the key slot */
        fetch: async (keySlot: number) => (await instance.get<IKeyTable>('/GetKeySlot', {
            params: {
                kioskKey: key,
                keySlotNumber: keySlot,
            },
        })).data,
        /** Fetches the number of free key slots */
        freeKeySlots: async () => (await instance.get<number>('/GetFreeKeySlots', { params: { kioskKey: key } })).data,
        /** Fetches the key table data of taken slots */
        takenKeySlots: async () => (await instance.get<IKeyTable[]>('/GetAppointmentKeySlots', { params: { kioskKey: key } })).data,
        /** Updates the key table entry */
        update: async (keySlot: number, storedByAdvisor?: boolean, appointmentKey?: IAppointment['key'], isKeyExchange?: boolean, isOccupied?: boolean) =>
            (
                await instance.post<IKeyTable>(
                    '/UpdateKeySlot',
                    {
                        keySlotNumber: keySlot,
                        isOccupied: isOccupied !== undefined ? isOccupied : !!appointmentKey,
                        appointmentKey,
                        storedByAdvisor: storedByAdvisor,
                        isKeyExchange: isKeyExchange,
                    },
                    { params: { kioskKey: key } },
                )
            ).data,
    };
};
// #endregion

// #region Transportation

export interface ITransporationOptionResponse extends ITransportationOptionResponseLocalized, ITransportationOptionResponseLocalizedAltLang {
    opCode: string;
    type: TransportType;
    subtype: TransportSubtype;
    showPickupTime: boolean;
    /** The DMS-sourced transportation classification (e.g. "Loaner", "Rental"). */
    dmsTransportationType: string;
}

interface ITransportationOptionResponseLocalized {
    closingMessage: string;
}

interface ITransportationOptionResponseLocalizedAltLang {
    closingMessageAltLang: string;
}

interface ITransportationOptionRoot {
    name: string;
    icon: IconTypes;
}

export interface ITransportationOption extends ITransportationOptionRoot, ITransporationOptionResponse { }
export type LocalizedTransportationOption = Omit<
    ITransportationOption,
    keyof ITransportationOptionResponseLocalized | keyof ITransportationOptionResponseLocalizedAltLang
> & {
    localization: ITransportationOptionResponseLocalized;
    full: ITransportationOption;
};

/**
 * Returns a factory that creates a LocalizedTransportationOption object from a ITransportationOption.
 */
export const useLocalizedTransportationOptionFactory = () => {
    const locale = useLocale();
    const {
        dealer: { primaryLanguage },
    } = useKioskState();

    return useCallback(
        (transportation: ITransportationOption): LocalizedTransportationOption => {
            if (primaryLanguage === locale) {
                const localization = {
                    closingMessage: transportation.closingMessage,
                };

                return {
                    ...transportation,
                    localization,
                    full: transportation,
                };
            }
            else {
                const localization = {
                    closingMessage: transportation.closingMessageAltLang || transportation.closingMessage,
                };

                return {
                    ...transportation,
                    localization,
                    full: transportation,
                };
            }
        },
        [locale, primaryLanguage],
    );
};

/**
 * Retrieves the valid transportation options for the kiosk.
 * @param kioskKey The kiosk Key
 * @param dealerKey The dealer Key
 */
export function useTransportationOptions(kioskKey: string, dealerKey: string) {
    const Localization = useLocalization();
    const factory = useLocalizedTransportationOptionFactory();
    const [loading, setLoading] = useState(false);
    const [serverOpts, setServerOpts] = useState<ITransporationOptionResponse[]>();

    const baseTransportationOptions: {
        [key in TransportType]: { [key in TransportSubtype]?: ITransportationOptionRoot };
    } = {
        [TransportType.drop]: {
            [TransportSubtype.none]: {
                name: Localization.DroppingOff,
                icon: 'Drop-Off',
            },
        },
        [TransportType.shuttle]: {
            [TransportSubtype.shuttle]: {
                name: Localization.formatString(Localization.Request, Localization.Shuttle) as string,
                icon: 'Shuttle',
            },
            [TransportSubtype.enterprise]: {
                name: `Enterprise ${Localization.RentalShuttle}`,
                icon: 'Enterprise-Shuttle',
            },
            [TransportSubtype.lyft]: {
                name: Localization.formatString(Localization.Request, 'Lyft') as string,
                icon: 'Lyft',
            },
            [TransportSubtype.uber]: {
                name: Localization.formatString(Localization.Request, 'Uber') as string,
                icon: 'Uber',
            },
        },
        [TransportType.wait]: {
            [TransportSubtype.none]: {
                name: Localization.WaitHere,
                icon: 'Waiter',
            },
        },
        [TransportType.loaner]: {
            [TransportSubtype.none]: {
                name: Localization.PrebookedRentalLoaner,
                icon: 'Loaner',
            },
        },
        [TransportType.notNeeded]: {
            [TransportSubtype.none]: {
                name: Localization.NotNeeded,
                icon: 'Close',
            },
        },
    };

    const { getLoanerOrRentalName } = useTransportLabels();

    useEffect(() => {
        if (!serverOpts && !loading) {
            (async () => {
                setLoading(true);
                const opts = (await instance.get<ITransporationOptionResponse[]>('/GetTransportationOptions', {
                    params: {
                        kioskKey,
                        dealerKey,
                    },
                })).data;
                setServerOpts(opts ?? []);
                setLoading(false);
            })();
        }
    }, [serverOpts, loading, kioskKey, dealerKey]);

    const transportationOptions = serverOpts?.map((opt) => {
        const base = baseTransportationOptions[opt.type][opt.subtype];

        const name: string | undefined = opt.type === TransportType.loaner
            ? getLoanerOrRentalName(opt.dmsTransportationType)
            : base?.name;

        return factory({
            ...opt,
            ...base,
            name,
        } as ITransportationOption);
    });

    return {
        loading,
        transportationOptions,
    };
}
// #endregion

// #region Advisor
export interface IServiceAdvisor {
    id: string;
    name: string | undefined;
}

/**
 * Gets the service advisors for the kiosk.
 * @param kioskKey The kiosk key
 * @param dealerKey The dealer key, if available
 * @param saNumber An additional service advisor number, if available
 * @param saName An additional service advisor name, if available
 * @param sendExistingAdvisorNumber If true, send the saNumber to indicate this appointment may use an advisor that is existing but inactive
 */
export const useServiceAdvisors = () => {

    const getServiceAdvisors = useCallback(async (kioskKey: string, dealerKey?: string, saNumber?: string, saName?: string, sendExistingAdvisorNumber?: boolean) => {
        const params = sendExistingAdvisorNumber ? {
            kioskKey,
            dealerKey,
            existingAdvisorNumber: saNumber,
        } : {
            kioskKey,
            dealerKey,
        };
        const response = (await instance.get<IServiceAdvisor[]>('/GetServiceAdvisors', { params }));
        // const [response] = useAxios<IServiceAdvisor[]>({ url: '/GetServiceAdvisors', params });

        if (saNumber && !response.data?.some((advisor) => advisor.id === saNumber)) {
            if (response.data == null) {
                response.data = [];
            }

            response.data.push({
                id: saNumber,
                name: saName,
            });
        }

        return response.data;
    }, []);

    return getServiceAdvisors;
};

/**
 * Gets the default service advisor for the kiosk
 * @param kioskKey the kiosk key
 * @param dealerKey the dealer key, if available
 */
export const getDefaultServiceAdvisor = async (kioskKey: Kiosk['key'], dealerKey?: Dealer['key']) =>
    (await instance.get<IServiceAdvisor>('/GetDealerDefaultAdvisor', {
        params: {
            kioskKey,
            dealerKey,
        },
    })).data;

// #endregion

// #region Vehicle

export const getVehicleYears = async (country: Country) => (await instance.get<number[] | null>('/GetVehicleYears', { params: { country } })).data?.sort((a, b) => b - a);

export const getVehicleMakes = async (kioskKey: Kiosk['key'], dealerKey: Dealer['key'], country: Country, year: string) =>
    (await instance.get<string[] | null>('/GetVehicleMakes', {
        params: {
            kioskKey,
            dealerKey,
            country,
            year,
        },
    })).data;

export const getVehicleModels = async (kioskKey: Kiosk['key'], dealerKey: Dealer['key'], country: Country, year: string, make: string) =>
    (await instance.get<string[] | null>('/GetVehicleModels', {
        params: {
            kioskKey,
            dealerKey,
            country,
            year,
            make,
        },
    })).data;

export const updateDMSVehicle = async (kioskKey: Kiosk['key'], appointment: IAppointment) =>
    (await instance.post<string>('/UpdateDmsVehicle', {
        ...appointment,
        kioskKey,
    }, { params: { kioskKey } })).data;
// #endregion

// #region Valuation

export interface IValuation {
    roughValue?: number;
    cleanValue?: number;
    averageValue?: number;
}

/**
 * Retrieves valuation data for the vehicle.
 * @param kioskKey The kiosk key
 * @param vin The vehicle VIN
 * @param mileage The vehicle mileage
 */
export const getValuationByVin = async (kioskKey: string, vin: string, mileage: number | null) => {
    const params = {
        kioskKey,
        vin,
        mileage,
    };

    return (await instance.get<IValuation>('/GetValuationByVin', { params })).data;
};

/**
 * Sends an email stating the customer has requested a valuation consultation.
 * @param appointmentKey The Appointment key
 * @param dealerKey The Dealer key
 */
export const sendValuationRequestedEmail = (appointmentKey: string, dealerKey: string) => {
    const params = {
        appointmentKey,
        dealerKey,
    };

    instance.get('/SendValuationRequestedEmail', { params });
};

// #endregion

// #region Banner Images
export const getKioskBannerImages = async (kioskKey: Kiosk['key']) =>
    (await instance.get<string[] | null>('/GetBannerImages', { params: { kioskKey } })
        .catch(() => ({ data: [] }))).data;
// #endregion

// #region SignalR
export enum SignalrEnrollment {
    LockerStatus = 'enrollForLockerStatus',
    ManuallyComplete = 'enrollForManuallyCompleteAppointment',
    AppointmentKeyPickedUp = 'enrollForAppointmentKeyPickedUp',
    AppointmentKeyStored = 'enrollForAppointmentKeyStored'
}

export enum SignalrEvent {
    LockerStatus = 'LockerStatus',
    ManuallyComplete = 'ManuallyComplete',
    AppointmentKeyStored = 'AppointmentKeyStored',
    AppointmentKeyPickedUp = 'AppointmentKeyPickedUp'
}
// #endregion

// #region Dev API Calls
export const getKioskList: GoMotoFunction<void, Promise<SelectOption[]>> = async () => {
    const response: AxiosResponse<SelectOption[]> = await instance.get<SelectOption[]>('/GetKioskList');

    return response.data;
};
// #endregion